import { Trans } from '@lingui/macro'
import { useQuery } from '@tanstack/react-query'
import { queryGetObject, queryGetObjects } from 'app/backend/Common'
import { objectTypeMappingFromSalesforce } from 'app/backend/salesforce-maps/SFObjectsMapping'
import { getAccessLevelToObject } from 'app/backend/selesforce-custom-queries/Access'
import { ReduxOrganization, ReduxState, ReduxUser } from 'app/redux/ReduxInterfaces'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import { opportunitiesStages } from 'app/services/sfAuth/sfData/sfOpportunity'
import { AlertBanner } from 'app/views/common/alerts/AlertBanner'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { formObjectsToConnect } from '../editor/FormWizard'
import { FormErrorPopUp } from '../FormErrorPopUp'
import { RestrictAccessMap } from '../types/FormTypes'
import { useFormContext } from './FormContext'

/*
This component is responsible for checking if user has constant edit access to connected object and is still being on account team.
*/
export const FormAccessGuard: React.FC<{ savingErrorOcurred: boolean, restrictAccessForRoles: RestrictAccessMap, setDisabled: (state: boolean) => void }> = ({ restrictAccessForRoles, children, setDisabled, savingErrorOcurred = false }) => {
  const { mainConnectedObject, connectedMap, formViewType } = useFormContext()
  const [wrongAccountRole, setWrongAccountRole] = useState<'warning' | 'prevent' | null>(null)
  const [initialAccessLevel, setInitialAccessLevel] = useState<'edit' | 'read' | 'no_access' | undefined>(undefined)
  const [accessLost, setAccessLost] = useState<boolean>(false)
  const organization = useSelector<ReduxState, ReduxOrganization>((state) => state.organization)
  const user = useSelector<ReduxState, ReduxUser>((state) => state.user)
  const history = useHistory()
  const { enqueueSnackbar } = useSnackbar()

  const { data, isSuccess, isInitialLoading, isFetched } = useQuery({
    queryKey: ['acc-team-member'],
    refetchOnWindowFocus: true,
    refetchInterval: 1 * 60 * 1000,
    refetchIntervalInBackground: true,
    staleTime: 1 * 60 * 1000,
    placeholderData: [],
    queryFn: async () => await queryGetObjects({
      type: 'Organization Team Member',
      searchProps: {
        AccountId: organization.id
      },
      select: [
        'UserId', 'AccountId', 'Id', 'AccountAccessLevel', 'TeamMemberRole', 'User.Email', 'User.Name'
      ]
    })
  })

  const accessLevelQuery = useQuery({
    queryKey: ['edit-access'],
    refetchOnWindowFocus: true,
    refetchInterval: 1 * 60 * 1000,
    refetchIntervalInBackground: true,
    staleTime: 1 * 60 * 1000,
    placeholderData: undefined,
    queryFn: async () => {
      const objectId = connectedMap != null && Object.values(connectedMap).find(obj => obj.objectType === mainConnectedObject)?.sfObject.Id
      if (objectId == null || objectId === false) {
        return undefined
      }

      const objectType = objectTypeMappingFromSalesforce[mainConnectedObject]
      if (objectType != null) {
        const select = formObjectsToConnect[mainConnectedObject]?.select
        const include = formObjectsToConnect[mainConnectedObject]?.include
        await queryGetObject({
          type: objectType,
          id: objectId,
          select,
          include
        }).then(result => {
          const obj = result
          if (mainConnectedObject === 'Opportunity') {
            if (obj.stageName !== opportunitiesStages.IN_PROGRESS &&
              obj.stageName !== opportunitiesStages.MORE_INFO_REQUIERED) {
              setDisabled(true)
            }
            if (Array.isArray(obj.processInstances) && obj.processInstances.some(process => process.status === 'Pending')) {
              enqueueSnackbar(<Trans>Application is Locked: Contact the Administrator</Trans>, { variant: 'error' })
              setDisabled(true)
            }
          } else if (mainConnectedObject === 'FGM_Base__Grantee_Report__c') {
            if (!['Requested', 'More info required'].includes(obj.status)) {
              setDisabled(true)
            }
          } else if (mainConnectedObject === 'Pre_Qualification__c') {
            if (obj.stage !== opportunitiesStages.IN_PROGRESS &&
              obj.stage !== opportunitiesStages.MORE_INFO_REQUIERED) {
              setDisabled(true)
            }
          } else if (mainConnectedObject === 'Resource__c') {
            if (
              obj.stage !== 'In progress' &&
            obj.stage !== opportunitiesStages.MORE_INFO_REQUIERED
            ) {
              setDisabled(true)
            }
          }
        })
      }

      return await getAccessLevelToObject({
        type: mainConnectedObject,
        id: objectId
      }).then(result => {
        if (result == null) {
          return 'no_access'
        }
        if (result.HasEditAccess === true) {
          return 'edit'
        } else if (result.HasReadAccess === true) {
          return 'read'
        } else {
          return 'no_access'
        }
      }, reject => {
        return 'no_access'
      })
    }
  })

  const userMember = data?.find(member => member.userId === user.userId)

  useEffect(() => {
    const userRole = userMember?.teamMemberRole
    if ((userRole != null && userRole in restrictAccessForRoles)) {
      const restrictType = restrictAccessForRoles[userRole]
      if (restrictType === 'preventAccessBlock') {
        enqueueSnackbar(<Trans>Your member role has insufficient access to view this page!</Trans>, { variant: 'error' })
        history.push('/grants/home')
      } else if (restrictType === 'disable') {
        setDisabled(true)
        setWrongAccountRole('warning')
      } else if (restrictType === 'preventAccessMessage') {
        setWrongAccountRole('prevent')
      }
    }
    if (userMember == null && !isInitialLoading && isFetched && !sfOauthConfig.isInternal) {
      // This means we lost access to the current org
      setDisabled(true)
      setAccessLost(true)
    }
  }, [userMember])

  useEffect(() => {
    if (initialAccessLevel == null) {
      setInitialAccessLevel(accessLevelQuery.data)
    } else if (formViewType === 'editable') {
      // User lost access to the data
      if (accessLevelQuery.data === 'read' && initialAccessLevel === 'edit') {
        setDisabled(true)
        setAccessLost(true)
      } else if (accessLevelQuery.data === 'no_access' && ['read', 'edit'].includes(initialAccessLevel)) {
        setDisabled(true)
        setAccessLost(true)
      }
    }
  }, [accessLevelQuery.data])

  if (wrongAccountRole === 'prevent') {
    return (
      <AlertBanner
        title={<Trans>FORMS_NO_VIEW_OR_EDIT_BANNER_TITLE</Trans>}
        description={<Trans>FORMS_REQUEST_ACCESS_BANNER_TEXT</Trans>}
      />
    )
  }

  return (
    <>
      {accessLost &&
        <FormErrorPopUp
          open={accessLost && !savingErrorOcurred}
          error={{
            message: 'ACCESS_LOST',
            type: 'Access'
          }}
          connectedMap={connectedMap}
          mainConnectedObject={mainConnectedObject}
          type='Unexpected error'
        />}
      {wrongAccountRole === 'warning' &&
        <AlertBanner
          title={<Trans>FORMS_NO_EDIT_BANNER_TITLE</Trans>}
          description={<Trans>FORMS_REQUEST_ACCESS_BANNER_TEXT</Trans>}
        />}
      {children}
    </>
  )
}
